import { useCallback, useEffect, useRef, useState } from 'react';
import { ExtractValueFromConfig, UseBroadcastValueConfig } from 'types';

/** for broadcaster components that expose some state to other tabs via broadcast channels */
export const useBroadcastState = <T extends UseBroadcastValueConfig>({
  channel,
  event,
  initialValue,
}: T) => {
  const [value, setValue] = useState(initialValue);
  const broadcastChannel = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    const pipe = new BroadcastChannel(channel);
    broadcastChannel.current = pipe;

    return () => {
      pipe.close();
    };
  }, [channel]);

  useEffect(() => {
    if (!broadcastChannel.current) {
      return;
    }
    broadcastChannel.current.onmessage = (ev) => {
      ev.data.type === event && setValue(ev.data.value);
    };
  }, [broadcastChannel, event, channel]);

  const setAndBroadcastValue = useCallback(
    (nextValue) => {
      if (!broadcastChannel.current) {
        return;
      }
      broadcastChannel.current.postMessage({ type: event, value: nextValue });
      setValue(nextValue);
    },
    [broadcastChannel, event]
  );

  return [value, setAndBroadcastValue] as [
    ExtractValueFromConfig<T>,
    (nextValue: ExtractValueFromConfig<T>) => void
  ];
};
